import { Domains } from 'monitoring/constants/domains'
import { Types } from 'monitoring/constants/types'
import { sendError } from 'monitoring/sendError/server/sendError'
import { sendLog } from 'monitoring/sendLog/server/sendLog'
import type { PageParams } from 'types/pageParams'
import { StatusCode } from 'types/statusCodes'
import { getHeaderAkamai } from 'utils/akamai'
import { getAkamaiFF } from 'utils/akamai/getAkamaiFF'
import { env } from 'utils/envManager'

import type { Product } from '../../../types'
import type { ProductDetails } from '../../../types/ProductDetails'
import {
	validateProductDetailsColors,
	validateProductDetailsErrors,
} from '../../../utils/validateProductDetailsErrors/validateProductDetailsErrors'

export interface GetProductDetailsProps {
	productId: Product['id']
	country: PageParams['country']
	language: PageParams['language']
}

export async function getProductDetails({
	productId,
	language,
	country,
}: GetProductDetailsProps): Promise<ProductDetails | null | void> {
	const { orchestrator: orchestratorFromAkamai } = getAkamaiFF()

	const baseUrl = `${orchestratorFromAkamai ? '/ws-online-orchestrator' : ''}/v1/products/${productId}/tags`

	const searchParams = new URLSearchParams({
		language,
		countryIso: country,
		channelId: env.NEXT_PUBLIC_CHANNEL,
	})

	const domain = orchestratorFromAkamai
		? env.NEXT_PUBLIC_SITE_BASE_URL
		: env.PRODUCT_SERVICE

	const url = new URL(baseUrl, domain)
	url.search = searchParams.toString()

	let headers = {}
	if (orchestratorFromAkamai) {
		headers = await getHeaderAkamai()
	}
	const response = await fetch(url.href, {
		headers,
		next: {
			revalidate: 0,
		},
	})

	if (response.ok) {
		const productDetails: ProductDetails = await response.json()

		const { hasError: hasColorsError } =
			validateProductDetailsColors(productDetails)

		if (hasColorsError) {
			throw sendError({
				domain: Domains.Product,
				message: '[ProductDetails] This product has no colors',
				custom: {
					productId,
					colors: null,
				},
			})
		}

		const colorsErrorsData = validateProductDetailsErrors(productDetails)

		if (colorsErrorsData) {
			sendLog({
				type: Types.Warning,
				domain: Domains.Product,
				message: '[ProductDetails] This product has some properties missing',
				custom: {
					productId,
					colors: colorsErrorsData,
				},
			})
		}

		return productDetails
	} else if (response.status === StatusCode.NOT_FOUND) {
		return null
	} else {
		throw sendError({
			domain: Domains.Product,
			message: '[ProductDetails] Failed to fetch product details',
			custom: { productId },
		})
	}
}
